import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Button } from '~common/components/button'
import { TextField } from '~common/components/text-field'
import { httpsCallable } from 'firebase/functions'
import { useAppContext } from '~/app-context'
import { LoadingIndicator } from '~common/components/loading'
import PricingComponent from '~/features/pricing'

interface GetMyInvoicesOnboardingProps {
    onClose: () => void
}

const MONTHS = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
]

export const GetMyInvoicesOnboarding: React.FC<
    GetMyInvoicesOnboardingProps
> = ({ onClose }) => {
    const { t } = useTranslation('settings')
    const { services, storageModules } = useAppContext()
    const cache = services.cache
    const entitlement = cache.getKey('entitlement')

    const [apiKey, setApiKey] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [step, setStep] = useState<
        | 'input'
        | 'date_selection'
        | 'rescan_option'
        | 'syncing'
        | 'success'
        | 'pricing'
    >('input')
    const [shouldRescan, setShouldRescan] = useState(false)

    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [selectedMonth, setSelectedMonth] = useState('01')
    const [documentCount, setDocumentCount] = useState<number | null>(null)

    const remainingCredits = entitlement
        ? entitlement.invoicesRemainingConsumable +
          entitlement.invoicesRemainingSubscription
        : 0

    const handleNextStep = async () => {
        if (!apiKey.trim()) {
            setError('API Key is required')
            return
        }
        setStep('date_selection')
    }

    const handleCheckDocuments = async () => {
        setIsLoading(true)
        setError(null)

        try {
            const startDate = `${selectedYear}-${selectedMonth}-01`
            const result = (await httpsCallable(
                services._functions,
                'countGetMyInvoicesDocuments',
            )({ apiKey, startDate })) as { data: { count: number } }

            setDocumentCount(result.data.count)
            setStep('rescan_option')
        } catch (err) {
            console.error('Failed to count documents:', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to check documents. Please check your API Key.',
            )
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveAndSync = async () => {
        setIsLoading(true)
        setError(null)

        try {
            // 1. Encrypt and save API key
            const encryptResponse = (await httpsCallable(
                services._functions,
                'encryptString',
            )({ text: apiKey })) as {
                data: { encrypted: string }
            }
            const encryptedApiKey = encryptResponse.data.encrypted

            const user = await storageModules.auth.getCurrentUser()
            if (!user) throw new Error('User not found')

            const settings = await services.cache.getSettings()
            const updatedPlugins = {
                ...settings?.plugins,
                'get-my-invoices': {
                    enabled: true,
                    apiKey: encryptedApiKey,
                },
            }

            await storageModules.auth.updateUser(user.id, {
                settings: {
                    plugins: updatedPlugins,
                },
            })

            // Update cache
            services.cache.updateSettings({
                plugins: updatedPlugins,
            })

            // 2. Trigger initial sync
            setStep('syncing')
            const startDate = `${selectedYear}-${selectedMonth}-01`
            await httpsCallable(
                services._functions,
                'syncGetMyInvoices',
            )({
                startDate: startDate,
                shouldRescan,
            })

            setStep('success')
        } catch (err) {
            console.error('Failed to enable GetMyInvoices:', err)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to enable plugin. Please check your API Key.',
            )
            setStep('input')
        } finally {
            setIsLoading(false)
        }
    }

    // Determine if credits are sufficient when rescan is enabled
    const isInsufficientCredits =
        shouldRescan &&
        documentCount !== null &&
        documentCount > remainingCredits

    return (
        <Container>
            {step === 'input' && (
                <>
                    <Title>Connect GetMyInvoices</Title>
                    <Description>
                        Please enter your GetMyInvoices API Key to start syncing
                        your bank accounts and invoices.
                    </Description>

                    <ApiKeyButtonContainer>
                        <Button
                            label="Get API Key"
                            onClick={() =>
                                window.open(
                                    'https://app.getmyinvoices.com/api_access',
                                    '_blank',
                                )
                            }
                            variant="secondary"
                            size="medium"
                        />
                    </ApiKeyButtonContainer>

                    <FormGroup>
                        <TextField
                            label="API Key"
                            initValue={apiKey}
                            onChange={setApiKey}
                            editable={!isLoading}
                            placeholder="Enter API Key"
                            width="100%"
                            size="large"
                        />
                    </FormGroup>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <Footer>
                        <Button
                            label="Cancel"
                            onClick={onClose}
                            variant="ghost"
                            size="medium"
                            disabled={isLoading}
                        />
                        <Button
                            label="Next"
                            onClick={handleNextStep}
                            variant="primary"
                            size="medium"
                            disabled={isLoading || !apiKey.trim()}
                        />
                    </Footer>
                </>
            )}

            {step === 'date_selection' && (
                <>
                    <Title>Import Documents From</Title>
                    <Description>
                        Select the start date for importing documents.
                    </Description>

                    <DateGroupContainer>
                        <YearSelect
                            value={selectedYear}
                            onChange={(e) =>
                                setSelectedYear(Number(e.target.value))
                            }
                            disabled={isLoading}
                        >
                            {Array.from(
                                { length: 10 },
                                (_, i) => new Date().getFullYear() - i,
                            ).map((year) => (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            ))}
                        </YearSelect>
                        <MonthSelect
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            disabled={isLoading}
                        >
                            {MONTHS.map((month) => (
                                <option key={month.value} value={month.value}>
                                    {month.label}
                                </option>
                            ))}
                        </MonthSelect>
                    </DateGroupContainer>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <Footer>
                        <Button
                            label="Back"
                            onClick={() => setStep('input')}
                            variant="ghost"
                            size="medium"
                            disabled={isLoading}
                        />
                        <Button
                            label={isLoading ? 'Checking...' : 'Next'}
                            onClick={handleCheckDocuments}
                            variant="primary"
                            size="medium"
                            disabled={isLoading}
                        />
                    </Footer>
                </>
            )}

            {step === 'rescan_option' && (
                <>
                    <Title>Do you want to rescan the imported invoices?</Title>
                    <Description>
                        {documentCount !== null ? documentCount : 'some'}{' '}
                        documents found since{' '}
                        {MONTHS.find((m) => m.value === selectedMonth)?.label}{' '}
                        {selectedYear}.
                    </Description>
                    <Description>
                        We noticed that the invoice extraction quality of
                        GetMyInvoices is poor
                        <br />
                        and leads to worse matches and more work for you.
                    </Description>

                    <Footer>
                        <Button
                            label="No"
                            onClick={() => setShouldRescan(false)}
                            variant={!shouldRescan ? 'primary' : 'secondary'}
                            size="medium"
                        />
                        <Button
                            label="Yes"
                            onClick={() => setShouldRescan(true)}
                            variant={shouldRescan ? 'primary' : 'secondary'}
                            size="medium"
                        />
                    </Footer>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '24px',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Button
                            label="Back"
                            onClick={() => setStep('date_selection')}
                            variant="ghost"
                            size="medium"
                            disabled={isLoading}
                        />
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}
                        >
                            {shouldRescan && documentCount !== null && (
                                <InfoText>
                                    Will cost {documentCount} credits
                                    {isInsufficientCredits &&
                                        ` but you only have ${remainingCredits} credits`}
                                </InfoText>
                            )}
                            {isInsufficientCredits ? (
                                <Button
                                    label="Add Credits"
                                    onClick={() => setStep('pricing')}
                                    variant="primary"
                                    size="medium"
                                    disabled={isLoading}
                                />
                            ) : (
                                <Button
                                    label={
                                        isLoading
                                            ? 'Connecting...'
                                            : 'Start Import'
                                    }
                                    onClick={handleSaveAndSync}
                                    variant="primary"
                                    size="medium"
                                    disabled={isLoading}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}

            {step === 'pricing' && (
                <div style={{ margin: '-24px', height: '600px' }}>
                    <PricingComponent
                        onClose={() => setStep('rescan_option')}
                    />
                </div>
            )}

            {step === 'syncing' && (
                <LoadingContainer>
                    <LoadingIndicator />
                    <LoadingText>
                        Syncing your data from GetMyInvoices...
                        <br />
                        This may take a few minutes.
                    </LoadingText>
                </LoadingContainer>
            )}

            {step === 'success' && (
                <SuccessContainer>
                    <SuccessIcon>🎉</SuccessIcon>
                    <SuccessTitle>Successfully Connected!</SuccessTitle>
                    <SuccessText>
                        Your bank accounts and invoices are being synced in the
                        background.
                    </SuccessText>
                    <Button
                        label="Done"
                        onClick={() => {
                            onClose()
                            window.location.reload()
                        }}
                        variant="primary"
                        size="medium"
                        disabled={isLoading}
                    />
                </SuccessContainer>
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[6]};
    min-width: 400px;
`

const Title = styled.h2`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    text-align: center;
`

const Description = styled.p`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    color: ${({ theme }) => theme.colors.text.primary};
    line-height: 1.5;
    text-align: center;
    white-space: pre-line;
`

const InfoText = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.info};
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.colors.warning};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    text-align: center;
`

const Footer = styled.div`
    display: flex;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[4]};
`

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[8]} 0;
`

const LoadingText = styled.p`
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.5;
`

const SuccessContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[4]} 0;
`

const SuccessIcon = styled.div`
    font-size: 48px;
`

const SuccessTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const SuccessText = styled.p`
    margin: 0;
    text-align: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.5;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
`

const DateGroupContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing[2]};
`

const YearSelect = styled.select`
    /* prettier-ignore */
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
        theme.spacing[3]};
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-width: 100px;

    &:disabled {
        background-color: ${({ theme }) => theme.colors.background.bg3};
        color: ${({ theme }) => theme.colors.text.tertiary};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.border.focus};
        outline-offset: 2px;
    }
`

const MonthSelect = styled.select`
    /* prettier-ignore */
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
        theme.spacing[3]};
    box-sizing: border-box;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    background-color: ${({ theme }) => theme.colors.background.bg1};
    color: ${({ theme }) => theme.colors.text.primary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    min-width: 80px;

    &:disabled {
        background-color: ${({ theme }) => theme.colors.background.bg3};
        color: ${({ theme }) => theme.colors.text.tertiary};
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:focus {
        outline: 2px solid ${({ theme }) => theme.colors.border.focus};
        outline-offset: 2px;
    }
`

const ApiKeyButtonContainer = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`
